<template>
  <div>
    <Menubar :model="menusRefs" class="p-menubar-fixed">
      <template #start>
        <router-link to="/">
          <div class="top-icons p-d-flex p-mr-2 p-ai-center">
            <img class="main-selo" alt="GovPass" />

            <img
              v-if="!!logoUrl"
              class="p-ml-2"
              style="height: 90%"
              :src="logoUrl"
              alt="Logo"
            />
          </div>
        </router-link>
      </template>
      <template #end>
        <div class="top-icons p-d-flex p-mr-2 p-ai-center" style="gap: 0.25rem">
          <div v-if="amIAllowed('view', 'painel') && false">
            <a
              class="p-menuitem-link"
              @click.stop="toggleComponentMenu"
              aria-label="Painel"
            >
              <painel />
            </a>
          </div>
          <div v-if="amIAllowed('view', 'notificacao')">
            <a
              class="p-menuitem-link"
              @click.stop="toggleComponentMenu"
              aria-label="Notificações"
            >
              <notificacao />
            </a>
          </div>
          <div
            class="nav-usuario-logado"
            v-if="usuarioLogado.login"
            :title="usuarioLogado.titulo"
            @click="toggleUserMenu"
          >
            <span class="nav-usuario-login">{{ usuarioLogado.login }}</span>
            <span class="nav-usuario-cliente" v-if="usuarioLogado.cliente">
              {{ usuarioLogado.cliente }}
            </span>
          </div>
          <div>
            <a
              class="p-menuitem-link nav-user-button"
              @click="toggleUserMenu"
              aria-label="Menu do usuário"
              role="button"
              tabindex="0"
            >
              <i class="pi pi-user" style="font-size: 1.1rem" />
            </a>
            <Menu
              id="overlay_menu"
              ref="menuUser"
              :model="menusUserRefs"
              :popup="true"
            />
          </div>
        </div>
      </template>
    </Menubar>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { MenuModel } from "../../router";
import Notificacao from "../notificacao/Notificacao.vue";
import Painel from "../painel/Painel.vue";

export default defineComponent({
  setup() {
    const { user } = useAuth();
    const initials = computed(
      () =>
        user?.value &&
        [user.value.userName?.substr(0, 1)].filter((e) => !!e).join("")
    );

    const logoUrl = computed(() => {
      return user?.value && user.value.logoUrl;
    });

    // Usuário logado ao lado do sino: facilita os testes e identifica o usuário nos prints que
    // os clientes enviam. O login é único; o cliente ajuda quando a GovPass opera vários.
    const usuarioLogado = computed(() => {
      const login = user?.value?.userName?.toLowerCase() || "";
      const cliente = user?.value?.nomeCliente || "";
      const nome = user?.value?.nome || "";
      return {
        login,
        cliente,
        titulo: [
          nome && nome.toLowerCase() !== login ? nome : "",
          login,
          cliente
        ]
          .filter((e) => !!e)
          .join(" · ")
      };
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const menus = [] as Array<MenuModel>;
    if (
      amIAllowed("view", "root-dashboard") ||
      amIAllowed("view", "root-cliente")
    ) {
      const menuRoot = {
        label: "Root",
        icon: "fas fa-brain",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "root-dashboard")) {
        menuRoot.items.push({
          label: "Dashboard",
          icon: "fas fa-tachometer-alt",
          to: { name: "root-dashboard" }
        });
        menuRoot.items.push({
          label: "Robôs & Filas",
          icon: "fas fa-robot",
          to: { name: "root-robos-filas" }
        });
      }
      if (amIAllowed("view", "root-cliente")) {
        menuRoot.items.push({
          label: "Clientes",
          icon: "fas fa-users",
          to: { name: "root-cliente" }
        });
      }
      if (amIAllowed("view", "root-dashboard")) {
        menuRoot.items.push({ separator: true });
        menuRoot.items.push({
          label: "Preços RNTRC",
          icon: "fas fa-tags",
          to: { name: "root-rntrc-preco" }
        });
        menuRoot.items.push({
          label: "Prestação de contas RNTRC",
          icon: "fas fa-file-invoice-dollar",
          to: { name: "root-rntrc-prestacao-contas" }
        });
      }
      menus.push(menuRoot);
    }

    if (
      amIAllowed("view", "licenca") ||
      amIAllowed("view", "licenca-combinacao-veiculos") ||
      amIAllowed("view", "relatorio-faturamento")
    ) {
      const menuLicenca = {
        label: "Licenças",
        icon: "fas fa-file-alt",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "licenca")) {
        menuLicenca.items.push({
          label: "Licenças",
          icon: "fas fa-file-alt",
          to: { name: "licenca" }
        });
      }
      // sem o submenu "Consultas" (15/10/2026): Licenças, Faturamento e Combinação no mesmo nível
      if (amIAllowed("view", "relatorio-faturamento")) {
        menuLicenca.items.push({
          label: "Faturamento",
          icon: "far fa-chart-bar",
          to: { name: "relatorio-faturamento" }
        });
      }
      if (amIAllowed("view", "licenca-combinacao-veiculos")) {
        menuLicenca.items.push({
          label: "Combinação de Veículos",
          icon: "fas fa-truck",
          to: { name: "licenca-combinacao-veiculos" }
        });
      }
      menus.push(menuLicenca);
    }

    if (
      amIAllowed("view", "veiculo-trator") ||
      amIAllowed("view", "carreta") ||
      amIAllowed("view", "veiculo-trator-modelo") ||
      amIAllowed("view", "veiculo-importacao-lote")
    ) {
      const menuVeiculo = {
        label: "Veículos",
        icon: "fas fa-truck",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "veiculo-trator-modelo")) {
        menuVeiculo.items.push({
          label: "Modelos",
          icon: "fas fa-file-invoice",
          to: { name: "veiculo-trator-modelo" }
        });
      }
      if (amIAllowed("view", "veiculo-trator")) {
        menuVeiculo.items.push({
          label: "Veículos Tratores",
          icon: "fas fa-truck",
          to: { name: "veiculo-trator" }
        });
      }

      if (amIAllowed("view", "carreta")) {
        menuVeiculo.items.push({ separator: true });
        menuVeiculo.items.push({
          label: "Carretas",
          icon: "fas fa-truck-loading",
          to: { name: "carreta" }
        });
      }
      if (amIAllowed("view", "veiculo-importacao-lote")) {
        menuVeiculo.items.push({ separator: true });
        menuVeiculo.items.push({
          label: "Atualização de documentos em lote",
          icon: "fas fa-file-import",
          to: { name: "veiculo-importacao-lote" }
        });
      }

      menus.push(menuVeiculo);
    }
    if (amIAllowed("view", "transportador")) {
      menus.push({
        label: "Transportadores",
        icon: "fas fa-briefcase",
        to: { name: "transportador" }
      });
    }
    if (amIAllowed("view", "engenheiro")) {
      menus.push({
        label: "Engenheiros",
        icon: "fas fa-hard-hat",
        to: { name: "engenheiro" }
      });
    }
    if (amIAllowed("view", "trechos-planilha")) {
      menus.push({
        label: "Planilhas de Trechos",
        icon: "fas fa-road",
        to: { name: "trechos-planilha" }
      });
    }

    if (
      amIAllowed("view", "orcamento") ||
      amIAllowed("view", "tabela-preco") ||
      amIAllowed("view", "contrato-transportador")
    ) {
      const menuOrcamento = {
        label: "Orçamentos",
        icon: "fas fa-dollar-sign",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "orcamento")) {
        menuOrcamento.items.push({
          label: "Pipeline de Orçamentos",
          icon: "fas fa-columns",
          to: { name: "orcamento" }
        });
      }
      if (
        (amIAllowed("view", "tabela-preco") ||
          amIAllowed("view", "contrato-transportador")) &&
        menuOrcamento.items.length > 0
      ) {
        menuOrcamento.items.push({ separator: true });
      }
      if (amIAllowed("view", "tabela-preco")) {
        menuOrcamento.items.push({
          label: "Tabela de Preço",
          icon: "fas fa-list-ol",
          to: { name: "tabela-preco" }
        });
      }
      if (amIAllowed("view", "contrato-transportador")) {
        menuOrcamento.items.push({
          label: "Contratos com Transportador",
          icon: "fas fa-handshake",
          to: { name: "contrato-transportador" }
        });
      }
      menus.push(menuOrcamento);
    }

    // RNTRC (ANTT): só a listagem e o faturamento; novas solicitações saem da listagem.
    const menuRntrc = {
      label: "RNTRC (ANTT)",
      icon: "fas fa-id-card",
      items: [] as Array<MenuModel>
    };
    if (amIAllowed("view", "rntrc")) {
      menuRntrc.items.push({
        label: "Solicitações",
        icon: "fas fa-list",
        to: { name: "rntrc-solicitacao" }
      });
      menuRntrc.items.push({ separator: true });
      menuRntrc.items.push({
        label: "Faturamento RNTRC",
        icon: "fas fa-file-invoice-dollar",
        to: { name: "rntrc-faturamento" }
      });
      menus.push(menuRntrc);
    }

    const menusUser = [] as Array<MenuModel>;
    if (amIAllowed("view", "admin-cliente")) {
      menusUser.push({
        label: "Configurações",
        icon: "fas fa-user-cog",
        to: { name: "admin-cliente" }
      });
    }
    if (amIAllowed("view", "usuario")) {
      menusUser.push({
        label: "Usuários",
        icon: "fas fa-users",
        to: { name: "usuario" }
      });
    }
    menusUser.push({
      label: "Minha Senha",
      icon: "fas fa-key",
      to: { name: "me-change-password" }
    });
    menusUser.push({
      label: "Sair",
      icon: "pi pi-fw pi-power-off",
      to: { name: "logout" }
    });

    const menusRefs = ref(menus);
    const menusUserRefs = ref(menusUser);
    const menuUser = ref();

    const toggleComponentMenu = (evt: Event) => {
      ((evt.target as HTMLElement)?.firstElementChild as HTMLElement)?.click();
    };
    const toggleUserMenu = (event: Event) => {
      menuUser.value.toggle(event);
    };

    return {
      user,
      initials,
      usuarioLogado,
      menusRefs,
      menusUserRefs,
      menuUser,
      logoUrl,
      toggleComponentMenu,
      toggleUserMenu,
      amIAllowed
    };
  },

  components: {
    Notificacao,
    Painel
  }
});
</script>

<style scoped lang="scss">
.nav-usuario-logado {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  max-width: 16rem;
  padding: 0 0.25rem;
  line-height: 1.2;
  cursor: pointer;
  text-align: right;

  span {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.nav-usuario-login {
  font-size: $font-size-sm;
  font-weight: 600;
  color: $text-primary;
}

.nav-usuario-cliente {
  font-size: $font-size-xs;
  color: $text-secondary;
}

@media (max-width: 576px) {
  .nav-usuario-cliente {
    display: none;
  }

  .nav-usuario-logado {
    max-width: 8rem;
  }
}
</style>
